import { flashbackSelfHost } from "@/data/flashback-selves";
import type { CharacterStay, RailLayout, RailBranch } from "@/data/rail-branches";
import { branchAt } from "../RailBranchMotion/motion";
import { forkCurveSpan, joinCurveSpan } from "../RailLanes/lanes";
import { staysOf } from "../StayAt/stay";

export interface CurveRide {
  /** The stay on the yarn they are riding toward, or back from. */
  stay: CharacterStay;
  branch: RailBranch;
  /** Who they slide out of, and back into. */
  hostId: string;
  /** 0 at the host's marker, 1 at their own seat on the split yarn. */
  t: number;
}

/**
 * A flashback self whose present self is on stage rides the split yarn up out of them before
 * its first scene, and back down into them after its last, instead of popping in or out.
 */
export function curveRideAt(
  rail: RailLayout,
  characterId: string,
  playheadMs: number,
): CurveRide | undefined {
  for (const stay of staysOf(rail.stays, characterId)) {
    if (playheadMs < stay.startMs) {
      const branch = branchAt(rail.branches, stay.rootPlaceId, stay.startMs);
      if (branch?.leadIn !== true || branch.startMs !== stay.startMs) {
        continue;
      }
      const fork = forkCurveSpan(branch);
      const ride = ridingWith(rail, stay, branch, playheadMs, fork, false);
      if (ride !== undefined) {
        return ride;
      }
    } else if (playheadMs >= stay.endMs) {
      const branch = branchAt(rail.branches, stay.rootPlaceId, stay.endMs - 1);
      if (branch?.leadOut !== true || branch.endMs !== stay.endMs) {
        continue;
      }
      const join = joinCurveSpan(branch);
      const ride = ridingWith(rail, stay, branch, playheadMs, join, true);
      if (ride !== undefined) {
        return ride;
      }
    }
  }
  return undefined;
}

function ridingWith(
  rail: RailLayout,
  stay: CharacterStay,
  branch: RailBranch,
  playheadMs: number,
  span: { startMs: number; endMs: number },
  back: boolean,
): CurveRide | undefined {
  if (playheadMs < span.startMs || playheadMs >= span.endMs) {
    return undefined;
  }
  const hostId = edgeHost(rail, stay, playheadMs, back ? "end" : "start");
  if (hostId === null) {
    return undefined;
  }
  const along = (playheadMs - span.startMs) / (span.endMs - span.startMs);
  return { stay, branch, hostId, t: back ? 1 - along : along };
}

/**
 * Whose marker someone comes out of, or goes back into, at a yarn's split or rejoin: their own
 * present self, or else the present self of someone splitting off or rejoining with them, as
 * Dude comes out of Tobias with Tobias's past self. Not an animal acquired there.
 */
export function edgeHost(
  rail: RailLayout,
  stay: CharacterStay,
  atMs: number,
  edge: "start" | "end",
): string | null {
  const own = flashbackSelfHost(rail.stays, stay.characterId, atMs);
  if (own !== null) {
    return own;
  }
  // Something acquired there, like the anole Jake catches, is found in the scene, not
  // brought along by whoever acquires it.
  const acquired = (rail.acquisitions ?? []).some((item) => (
    item.fromCharacterId === stay.characterId
    && item.startMs < stay.endMs && stay.startMs < item.endMs
  ));
  if (acquired) {
    return null;
  }
  for (const other of rail.stays) {
    const together = other.rootPlaceId === stay.rootPlaceId
      && other.characterId !== stay.characterId
      && (edge === "start" ? other.startMs === stay.startMs : other.endMs === stay.endMs);
    if (!together) {
      continue;
    }
    const host = flashbackSelfHost(rail.stays, other.characterId, atMs);
    if (host !== null) {
      return host;
    }
  }
  return null;
}

export interface Merge {
  hostId: string;
  /** 0 on their own seat, 1 inside the host's marker. */
  t: number;
}

/** Who someone slides back into as their yarn rejoins inside its last scene. */
export function mergeHostAtEnd(
  rail: RailLayout,
  stay: CharacterStay,
  branch: RailBranch | undefined,
): string | null {
  if (branch?.joinToLane === undefined || branch.leadOut === true || branch.endMs !== stay.endMs) {
    return null;
  }
  return edgeHost(rail, stay, stay.endMs, "end");
}

export function mergingInto(
  rail: RailLayout,
  stay: CharacterStay,
  branch: RailBranch | undefined,
  playheadMs: number,
): Merge | undefined {
  if (branch === undefined) {
    return undefined;
  }
  const join = joinCurveSpan(branch);
  if (playheadMs < join.startMs) {
    return undefined;
  }
  const hostId = mergeHostAtEnd(rail, stay, branch);
  if (hostId === null) {
    return undefined;
  }
  return { hostId, t: (playheadMs - join.startMs) / (join.endMs - join.startMs) };
}
